/**
 * Enigma Ledger — Cloud (Vite + TypeScript)
 *
 * Entry point. Boots auth, hydrates the DB + Prefs cache, mounts the app
 * shell, then defers actual rendering to per-section view modules registered
 * on the `sectionViews` map exported by state.ts.
 */
import "./styles.css";
import { primeIdPools } from "./utils/ids";
import { startPresence } from "./lib/presence";
import { icon } from "./utils/icon";
import { initCloudAuth, cloudSignOut } from "./lib/auth";
import { escapeHtml, toast } from "./lib/dom";
import { DB } from "./lib/db";
import { Settings } from "./lib/prefs";
import { SECTIONS, State, render, toggleSidebar, sectionViews, primaryActions } from "./state";
import { viewDashboard } from "./views/dashboard";
import { viewSealed, editSealed } from "./views/sealed";
import { viewCards, editCard } from "./views/cards";
import { viewSales } from "./views/sales";
import { viewPurchases } from "./views/purchases";
import { viewTrades } from "./views/trades";
import { viewGrading } from "./views/grading";
import { viewRips } from "./views/rips";
import { viewTransactions, editTxn } from "./views/transactions";
import { viewExpenses, editExpense } from "./views/expenses";
import { viewMileage, editMileage } from "./views/mileage";
import { viewBuys, editBuy } from "./views/buys";
import { viewLookup } from "./views/lookup";
import { viewEmployees, editEmployee } from "./views/employees";
import { showLotHistory } from "./components/lotHistory";
import { openConfigsMenu } from "./components/configs";
import { closeModal } from "./components/modal";
import { applyUserPrefs, openPreferences, openHelp, openAbout, openFeedback, openChangePassword } from "./components/userMenu";
import { initThumbHover } from "./components/thumbHover";
import { shouldAutoRefresh, runAutoFmvSweep, zeroEmptyLotFMV } from "./lib/fmvRefreshAll";
import { migrateReceiptsToStorage } from "./lib/migrate";
import { reallocateRipBasis, reallocateAllRipBases, sourceIdIsRip } from "./lib/ripBasis";
import { openRipBasisRepair, ripBasisRepairCount, ripBasisRepairPromptedV1, markRipBasisRepairPrompted } from "./forms/ripBasisRepair";
import "./components/booksHealthModal";
import { backfillTaxFields } from "./lib/taxBackfill";
import { reverseTransaction } from "./lib/inventoryEffects";
import type { TableKey } from "./lib/types";
// Side-effect imports so each module's window.* bridges install at load time.
import "./components/configs";
import "./components/fmvPicker";       // refreshSealedFMV / refreshCardFMV / refreshFMVInForm
import "./components/quickCardLookup"; // quickCardLookup / pickCardFromQuick / closeQuickLookup
import "./components/printReport";     // exportCardsReport / exportSealedReport
import "./components/userMenu";        // openPreferences / openHelp / openAbout / openFeedback
// import "./components/cardScanner"; // parked — see cardScanner.ts; revisit with AI Vision API later
import { editSale }     from "./forms/sale";
import { editPurchase } from "./forms/purchase";
import { editTrade }    from "./forms/trade";
import { editGrading }  from "./forms/grading";
import { startRip }     from "./forms/rip";
import { maybeRunScheduledBackup } from "./lib/backup";

// Register every section view. The renderer's fallback now only fires for
// truly unknown section IDs (chunk 5 will replace these with form-aware variants).
sectionViews["dashboard"]    = viewDashboard;
sectionViews["sealed"]       = viewSealed;
sectionViews["cards"]        = viewCards;
sectionViews["rips"]         = viewRips;
sectionViews["sales"]        = viewSales;
sectionViews["purchases"]    = viewPurchases;
sectionViews["trades"]       = viewTrades;
sectionViews["transactions"] = viewTransactions;
sectionViews["grading"]      = viewGrading;
sectionViews["expenses"]     = viewExpenses;
sectionViews["mileage"]      = viewMileage;
sectionViews["buys"]         = viewBuys;
sectionViews["lookup"]       = viewLookup;
sectionViews["employees"]    = viewEmployees;

// Header "+ Add X" buttons dispatch through this registry. Keys must match the
// `primary.key` strings in SECTIONS (state.ts). Anything not registered here
// falls through to a "coming in a later migration chunk" toast.
primaryActions["addSealed"]   = () => editSealed();
primaryActions["addCard"]     = () => editCard();
primaryActions["addTxn"]      = () => editTxn();
primaryActions["addExpense"]  = () => editExpense();
primaryActions["addMileage"]  = () => editMileage();
primaryActions["addBuy"]      = () => editBuy();
primaryActions["addSale"]     = () => editSale();
primaryActions["addPurchase"] = () => editPurchase();
primaryActions["addTrade"]    = () => { const a = document.getElementById("primaryAction"); if (a) openTradeAddMenu(a); };
primaryActions["addGrading"]  = () => editGrading();
primaryActions["addRip"]      = () => startRip();
primaryActions["addEmployee"] = () => editEmployee();

/* ============================================================
   Inline-handler bridges
   ============================================================ */
const win = window as unknown as Record<string, unknown>;

// Live implementations
win.showLotHistory = showLotHistory;

// Generic delete for keyed tables (Sealed / Cards / Sales / Purchases / Trades / Grading / Rips / Adjustments)
win.deleteRow = (table: string, idField: string, id: string) => {
  if (!confirm("Delete this row? This cannot be undone.")) return;
  // Transaction tables: reverse their recorded inventory footprint before
  // removal so lots/pools are restored. The orphan guard can block (e.g. a
  // trade whose incoming lot was later sold) — abort with the reason if so.
  if (table === "sales" || table === "trades" || table === "rips" || table === "grading" || table === "transactions") {
    const res = reverseTransaction(table as TableKey, id, idField);
    if (!res.ok) { toast("Can't delete: " + (res.blockedBy || "blocked by a later transaction"), "err"); return; }
  }
  // If we're deleting a card that was pulled from a rip, capture the rip ID
  // BEFORE the row vanishes so we can recompute basis allocation for the
  // remaining pulls afterwards.
  let ripToReallocate: string | null = null;
  if (table === "cards") {
    const row = DB.table("cards").find(r => r["Card Lot ID"] === id);
    const src = String(row?.["Source ID"] || "");
    if (src && sourceIdIsRip(src)) ripToReallocate = src;
  }
  DB.remove(table as any, idField, id);
  if (ripToReallocate) reallocateRipBasis(ripToReallocate);
  toast("Deleted");
  closeModal();
  // Re-render to drop the row from view; need dynamic import to avoid an extra top-level import.
  (window as any).render?.();
};

// All multi-item forms now live (their modules install their own window bridges
// at load time via the imports above).

/** Popover anchored under the "+ New Trade" primary button. Lets the user
 *  pick whether to propose a draft or log an already-accepted swap. The
 *  dismiss behaviour mirrors the user menu. */
function openTradeAddMenu(anchor: HTMLElement): void {
  const existing = document.getElementById("tradeAddMenu");
  if (existing) { existing.remove(); return; }
  const rect = anchor.getBoundingClientRect();
  const pop = document.createElement("div");
  pop.id = "tradeAddMenu";
  pop.className = "fixed panel p-1 z-[70] shadow-2xl min-w-[220px]";
  pop.style.top   = (rect.bottom + 4) + "px";
  pop.style.right = (window.innerWidth - rect.right) + "px";
  pop.innerHTML = `
    <button class="nav-item w-full text-left" data-trade-add="accepted"><span class="inline-flex w-5 justify-center">${icon("dollar-sign", { size: 16 })}</span><span>+ Log Completed Trade</span></button>
    <button class="nav-item w-full text-left" data-trade-add="proposed"><span class="inline-flex w-5 justify-center">${icon("message-square", { size: 16 })}</span><span>+ New Pending Trade</span></button>`;
  document.body.appendChild(pop);
  pop.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-trade-add]");
    if (!t) return;
    const kind = t.getAttribute("data-trade-add");
    pop.remove();
    if (kind === "proposed") editTrade(undefined, { seedStatus: "Proposed" });
    else editTrade();
  });
  const dismiss = (e: MouseEvent) => {
    if (!pop.contains(e.target as Node) && e.target !== anchor && !anchor.contains(e.target as Node)) {
      pop.remove();
      document.removeEventListener("mousedown", dismiss);
    }
  };
  setTimeout(() => document.addEventListener("mousedown", dismiss), 0);
}

/** Popover anchored under the username chip. Contains the user-level menu
 *  items (Preferences / Help / About / Feedback / Sign out). Clicking the
 *  anchor while the popover is already open toggles it closed. */
function openUserMenu(anchor: HTMLElement, userEmail: string): void {
  const existing = document.getElementById("userMenuPopover");
  if (existing) { existing.remove(); return; }
  const rect = anchor.getBoundingClientRect();
  const pop = document.createElement("div");
  pop.id = "userMenuPopover";
  pop.className = "fixed panel p-1 z-[70] shadow-2xl min-w-[220px]";
  pop.style.top   = (rect.bottom + 4) + "px";
  pop.style.right = (window.innerWidth - rect.right) + "px";
  pop.innerHTML = `
    <div class="px-3 py-2 text-xs text-[var(--muted)] border-b border-[var(--border)] truncate" title="${escapeHtml(userEmail)}">${escapeHtml(userEmail)}</div>
    <button class="nav-item w-full text-left" data-user="prefs"><span class="inline-flex w-5 justify-center">${icon("settings", { size: 16 })}</span><span>Preferences</span></button>
    <button class="nav-item w-full text-left" data-user="changepw"><span class="inline-flex w-5 justify-center">${icon("key", { size: 16 })}</span><span>Change password</span></button>
    <button class="nav-item w-full text-left" data-user="help"><span class="inline-flex w-5 justify-center">${icon("help-circle", { size: 16 })}</span><span>Help &amp; Shortcuts</span></button>
    <button class="nav-item w-full text-left" data-user="about"><span class="inline-flex w-5 justify-center">${icon("info", { size: 16 })}</span><span>About</span></button>
    <button class="nav-item w-full text-left" data-user="feedback"><span class="inline-flex w-5 justify-center">${icon("message-square", { size: 16 })}</span><span>Send Feedback</span></button>
    <div class="border-t border-[var(--border)] my-1"></div>
    <button class="nav-item w-full text-left" data-user="signout"><span class="inline-flex w-5 justify-center">${icon("log-out", { size: 16 })}</span><span>Sign out</span></button>`;
  document.body.appendChild(pop);
  pop.addEventListener("click", (e) => {
    const key = (e.target as HTMLElement).closest("[data-user]")?.getAttribute("data-user");
    if (!key) return;
    pop.remove();
    if (key === "prefs")    openPreferences();
    if (key === "changepw") openChangePassword();
    if (key === "help")     openHelp();
    if (key === "about")    openAbout();
    if (key === "feedback") openFeedback();
    if (key === "signout") {
      if (confirm("Sign out of Enigma Ledger?")) cloudSignOut();
    }
  });
  const dismiss = (e: MouseEvent) => {
    if (!pop.contains(e.target as Node) && e.target !== anchor && !anchor.contains(e.target as Node)) {
      pop.remove();
      document.removeEventListener("mousedown", dismiss);
    }
  };
  setTimeout(() => document.addEventListener("mousedown", dismiss), 0);
}

/* ============================================================
   Global guard: stop the mouse wheel from silently stepping a
   focused <input type="number"> by its step (e.g. a typed 1404
   becoming 1403.99 on save). When a number field has focus and the
   user scrolls over it, blur it so the page scrolls and the value
   stays exactly as typed. Arrow keys + spinner buttons still work
   for deliberate stepping.
   ============================================================ */
function installNumberInputWheelGuard(): void {
  if ((window as any).__numWheelGuard) return;
  (window as any).__numWheelGuard = true;
  document.addEventListener("wheel", (e) => {
    const el = document.activeElement as HTMLInputElement | null;
    if (el && el.tagName === "INPUT" && el.type === "number" &&
        (e.target === el || el.contains(e.target as Node))) {
      el.blur();
    }
  }, { passive: true });
}

function mountShell(userEmail: string): void {
  const root = document.getElementById("app");
  if (!root) return;
  root.innerHTML = `
    <div class="h-screen flex">
      <div id="sidebarOverlay" class="sidebar-overlay"></div>
      <aside id="sidebar" class="w-64 p-3 border-r border-[var(--border)] bg-[var(--panel)] flex flex-col gap-1 shrink-0">
        <div class="logo-area inline-flex items-center h-20 -ml-3 overflow-hidden">
          <img src="/logo.png" alt="" class="h-24 w-auto shrink-0"
               onerror="this.style.display='none'; this.nextElementSibling.style.display='block'; this.parentElement.nextElementSibling.style.display='flex'">
          <img src="/logo2.png" alt="Enigma Ledger" class="h-44 w-auto shrink-0 -ml-10">
        </div>
        <div class="hidden items-center gap-3" style="display:none">
          <div class="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center font-bold text-white">E</div>
          <div class="font-semibold leading-tight">Enigma Ledger</div>
        </div>
        <nav id="nav" class="flex flex-col gap-1 overflow-y-auto"></nav>
        <div class="mt-auto p-1 text-xs text-[var(--muted)]" id="lastSavedNote"></div>
      </aside>
      <div class="flex-1 flex flex-col min-w-0">
        <header class="flex items-center gap-2 px-4 py-3 border-b border-[var(--border)] shrink-0">
          <button class="btn btn-ghost !py-1 !px-2 md:hidden" id="btnSidebarToggle" title="Menu">${icon("menu", { size: 18 })}</button>
          <button class="btn btn-ghost !py-1 !px-2 hidden md:inline-flex" id="btnSidebarDesktop" title="Toggle sidebar">${icon("menu", { size: 18 })}</button>
          <h1 id="pageTitle" class="text-xl font-bold tracking-tight border-l-[3px] border-[var(--gold)] pl-3 truncate"></h1>
          <div class="ml-auto flex items-center gap-2">
            <button id="primaryAction" class="btn btn-primary" style="display:none"></button>
            <button id="btnUserMenu" class="btn btn-ghost !py-1 !px-2 text-xs hidden sm:inline-flex items-center gap-1 text-[var(--muted)]" title="Account menu">
              <span class="truncate max-w-[180px]">${escapeHtml(userEmail)}</span>
              <span class="opacity-70 inline-flex">${icon("chevron-down", { size: 12 })}</span>
            </button>
            <button id="btnConfigs" class="btn btn-ghost" title="Configs">${icon("settings", { size: 18 })}</button>
          </div>
        </header>
        <main id="view" class="flex-1 overflow-auto p-4 sm:p-6"></main>
      </div>
    </div>
  `;

  document.getElementById("sidebarOverlay")?.addEventListener("click", () => toggleSidebar(false));
  document.getElementById("btnSidebarToggle")?.addEventListener("click", () => toggleSidebar());
  document.getElementById("btnSidebarDesktop")?.addEventListener("click", () => toggleSidebar());
  const userBtn = document.getElementById("btnUserMenu");
  userBtn?.addEventListener("click", () => openUserMenu(userBtn, userEmail));
  const cfgBtn = document.getElementById("btnConfigs");
  cfgBtn?.addEventListener("click", () => openConfigsMenu(cfgBtn));

  // Apply persisted sidebar state
  const s = document.getElementById("sidebar");
  if (s) {
    if (window.innerWidth < 768) s.classList.add("is-collapsed");
    else if (localStorage.getItem("enigma_sidebar_collapsed") === "1") s.classList.add("is-collapsed");
  }

  // Initial section: respect URL hash if present, else fall back to the
  // user's preferred landing page (default "dashboard").
  const hashSection = (location.hash || "").slice(1);
  const landing = Settings.get().defaultLandingPage || "dashboard";
  const initial = SECTIONS.find(x => x.id === hashSection) ? hashSection
                : (SECTIONS.find(x => x.id === landing) ? landing : "dashboard");
  State.section = initial;

  render();
}

// Employee time portal (public, password-gated via Edge Function) runs INSTEAD
// of the owner app - branch before auth so the login overlay never mounts and
// no owner data loads. Dynamic import keeps it out of the main boot path.
const _portalMode = (location.hash || "").replace(/^#\/?/, "") === "time-portal";
if (_portalMode) {
  void import("./views/timePortal").then(m => m.mountTimePortal());
}

if (!_portalMode) initCloudAuth(async (email) => {
  try {
    await DB.init();
  } catch (e: any) {
    console.error("DB.init failed:", e);
    toast("Failed to load data: " + (e?.message || e), "err");
    return;
  }
  // Heal any rips logged before basis-allocation was implemented (or where
  // pulled-card FMVs have shifted since last reallocation). Idempotent —
  // skips rips whose allocation already matches.
  try {
    const healed = reallocateAllRipBases();
    if (healed > 0) console.info(`[ripBasis] reallocated ${healed} card lot(s) at load`);
  } catch (e) { console.error("[ripBasis] heal failed:", e); }

  // One-time backfill of holding-period / realized-gain on sales & trades that
  // predate Parts 3/4. Idempotent — skips already-classified items.
  try {
    const n = backfillTaxFields();
    if (n > 0) console.info(`[taxBackfill] classified ${n} transaction(s) at load`);
  } catch (e) { console.error("[taxBackfill] failed:", e); }

  // Heal sold/traded-out lots (Qty 0) that a prior FMV bug left with a phantom
  // non-zero Current FMV, which inflated inventory/FMV totals.
  try {
    const z = zeroEmptyLotFMV();
    if (z > 0) console.info(`[fmvHeal] zeroed FMV on ${z} empty lot(s) at load`);
  } catch (e) { console.error("[fmvHeal] failed:", e); }

  // Apply persisted user prefs (theme, text size) to the document root before
  // mounting the shell, so the initial render uses the right palette.
  applyUserPrefs();

  // Run a scheduled local backup if one is due (writes to the user's chosen folder).
  void maybeRunScheduledBackup();
  // Wire the global thumbnail hover-zoom listener (idempotent — installs once).
  initThumbHover();
  installNumberInputWheelGuard();
  // Auto FMV sweep: silently refresh prices if it's been >24h since the last run.
  if (shouldAutoRefresh()) {
    setTimeout(() => runAutoFmvSweep(), 2500);
  }
  migrateReceiptsToStorage().catch(e => console.error("[receipt-migration]", e));
  mountShell(email);
  // Reserve server-minted id blocks (Tier 0: no cross-device collisions).
  void primeIdPools();
  // Heartbeat + warn when the same account is active elsewhere (Tier 0).
  startPresence();

  // QR label deep link: scanning a printed label opens <app>#lot=<id> — pop
  // that lot's drawer once the shell is up, then clean the URL.
  {
    const m = (location.hash || "").match(/^#lot=([A-Za-z0-9_-]+)$/);
    if (m) {
      const lotId = decodeURIComponent(m[1]!);
      history.replaceState(null, "", location.pathname + location.search);
      setTimeout(() => {
        const kind = lotId.startsWith("S-") ? "sealed" : "card";
        (window as any).openLotDrawer?.(kind, lotId);
      }, 700);
    }
  }

  // One-time, reviewed cost-basis repair offer for ripped cards. A now-fixed
  // allocation bug double-counted box cost across pulls; this offers to correct
  // the basis/realized gain booked on past sales & trades. Prompts once; the
  // same review is always available in Configs -> Data Management.
  try {
    if (!ripBasisRepairPromptedV1()) {
      const n = ripBasisRepairCount();
      markRipBasisRepairPrompted();
      if (n > 0) {
        setTimeout(() => {
          if (confirm(`Cost-basis fix: ${n} past sale/trade line(s) for ripped cards have a mis-stated cost basis from a now-fixed allocation bug. Open the review screen to see the proposed corrections? (You can also run this anytime from Configs -> Data Management.)`)) {
            openRipBasisRepair();
          }
        }, 1200);
      }
    }
  } catch (e) { console.error("[ripBasisRepair] prompt failed:", e); }
});
